import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import './CoachingHistory.scss';
import { useGetCoachingHistoryQuery } from '../coachingActionPlanSlice';
import { SessionReadOnlyModal } from './SessionReadOnlyModal';

interface CoachingHistoryProps {
    coachingActionPlanId: number;
    CAPSessionData?: any;
}

interface CoachingHistoryItem {
    coaching_action_plan_id: number;
    employee_number: string;
    coaching_start_date: string;
    coaching_action_plan_status_name: string;
    session_number: number;
    session_start_date: string;
    session_id: number;
    commitment_attained: boolean;
    follow_up_date: string;
}

interface SelectedSession {
    sessionId: number;
    sessionTitle: string;
}

const getSessionTitle = (sessionNumber: number): string => {
    if (sessionNumber === 0) return 'Coaching Started';
    const ordinals = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth'];
    return `${ordinals[sessionNumber - 1] ?? `${sessionNumber}th`} Follow Up`;
};

const getStatusLabel = (
    sessionNumber: number,
    commitmentAttained: boolean,
    statusName: string,
    isLast: boolean
): string | null => {
    if (sessionNumber === 0) return null;
    if (statusName === 'Closed') {
        return commitmentAttained ? 'Commitment Attained' : 'Completed - Unsuccessful';
    }
    if (statusName === 'Open') {
        if (!isLast) return commitmentAttained ? 'Successful' : 'Unsuccessful'; 
        return commitmentAttained ? 'In Progress - Successful' : 'In Progress - Unsuccessful';
    }
    return null;
};

const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate();
    let suffix = 'th';
    if (day === 1 || day === 21 || day === 31) suffix = 'st';
    else if (day === 2 || day === 22) suffix = 'nd';
    else if (day === 3 || day === 23) suffix = 'rd';
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    return `${month} ${day}${suffix}, ${year}`;
};

const formatMonthYear = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

export const CoachingHistory: React.FC<CoachingHistoryProps> = ({ coachingActionPlanId, CAPSessionData }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [selectedSession, setSelectedSession] = useState<SelectedSession | null>(null);

    const { data, isLoading } = useGetCoachingHistoryQuery(coachingActionPlanId, {
        skip: !coachingActionPlanId || coachingActionPlanId <= 0,
    });

    const historyData: CoachingHistoryItem[] = data?.data ?? [];
    const startDate = historyData[0]?.coaching_start_date ?? null;

    const handleSessionClick = (item: CoachingHistoryItem) => {
        setSelectedSession({
            sessionId: item.session_id,
            sessionTitle: getSessionTitle(item.session_number),
        });
    };

    return (
        <div className={`coaching-history ${isExpanded ? 'coaching-history--expanded' : 'coaching-history--collapsed'}`}>

            {/* ── EXPANDED ── */}
            {isExpanded && (
                <>
                    <div className="coaching-history__header">
                        <div className="coaching-history__header-info">
                            <h3 className="coaching-history__title">Coaching History</h3>
                            {startDate && (
                                <p className="coaching-history__subtitle">
                                    Started on {formatMonthYear(startDate)}
                                </p>
                            )}
                        </div>
                        <button
                            className="coaching-history__toggle-btn"
                            onClick={() => setIsExpanded(false)}
                            title="Collapse history"
                        >
                            <i className="pi pi-chevron-left" />
                        </button>
                    </div>

                    <div className="coaching-history__body">
                        {isLoading && (
                            <p className="coaching-history__status-text">Loading...</p>
                        )}

                        {!isLoading && historyData.length === 0 && (
                            <p className="coaching-history__status-text">
                                No coaching history available
                            </p>
                        )}

                        {!isLoading && historyData.length > 0 && (
                            <div className="coaching-history__timeline">
                                {historyData.map((item, index) => {
                                    const isStarted = item.session_number === 0;
                                    const isLast = index === historyData.length - 1;
                                    const isActive = isLast && item.coaching_action_plan_status_name === 'Open';
                                    const statusLabel = getStatusLabel(
                                        item.session_number,
                                        item.commitment_attained,
                                        item.coaching_action_plan_status_name,
                                        isLast
                                    );

                                    return (
                                        <div
                                            key={`${item.session_id}-${index}`}
                                            className="coaching-history__item"
                                        >
                                            <button
                                                type="button"
                                                className={`coaching-history__card coaching-history__card--clickable ${isActive ? 'coaching-history__card--active' : ''}`}
                                                onClick={() => handleSessionClick(item)}
                                            >
                                                <div className="coaching-history__card-top">
                                                    <div className={`coaching-history__number ${isStarted ? 'coaching-history__number--purple' : 'coaching-history__number--blue'}`}>
                                                        {item.session_number}
                                                    </div>
                                                    <div className="coaching-history__card-content">
                                                        <div className={`coaching-history__card-title ${isActive ? 'coaching-history__card-title--active' : ''}`}>
                                                            {getSessionTitle(item.session_number)}
                                                        </div>
                                                        <div className={`coaching-history__card-date ${isActive ? 'coaching-history__card-date--active' : ''}`}>
                                                            {formatDate(item.session_start_date)}
                                                        </div>
                                                        {statusLabel && (
                                                            <div className={`coaching-history__card-status ${item.commitment_attained ? 'coaching-history__card-status--success' : 'coaching-history__card-status--danger'}`}>
                                                                {statusLabel}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </button>

                                            {!isLast && (
                                                <div className="coaching-history__connector" />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* ── COLLAPSED ── */}
            {!isExpanded && (
                <div className="coaching-history__collapsed-inner">
                    <button
                        className="coaching-history__toggle-btn"
                        onClick={() => setIsExpanded(true)}
                        title="Expand history"
                    >
                        <i className="pi pi-chevron-right" />
                    </button>
                    <div className="coaching-history__rotated-label">
                        <span className="coaching-history__rotated-title">Coaching History</span>
                        {startDate && (
                            <span className="coaching-history__rotated-sub">
                                Started on {formatMonthYear(startDate)}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* ── SESSION READ-ONLY MODAL ── */}
            {selectedSession &&
                ReactDOM.createPortal(
                    <SessionReadOnlyModal
                        sessionId={selectedSession.sessionId}
                        sessionTitle={selectedSession.sessionTitle}
                        CAPSessionData={CAPSessionData}
                        onClose={() => setSelectedSession(null)}
                    />,
                    document.body
                )}
        </div>
    );
};